<script setup>
import { onMounted, reactive, ref } from 'vue'
import axios from '@/axios'
import { useNotificationStore } from '@/stores/notifications'
import Modal from '@/components/Modal.vue'

const props = defineProps({
  show: Boolean,
  review: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['close', 'saved'])
const notifications = useNotificationStore()
const sourceTypes = ref([])
const qualities = ref([])
const saving = ref(false)

const form = reactive({
  source_type_code: null,
  title: '',
  author: '',
  url: '',
  citation_text: '',
  locator: '',
  excerpt: '',
  quality_code: 0
})

onMounted(async () => {
  const [sourceResponse, qualityResponse] = await Promise.all([
    axios.get('/api/dropdown_options/source_types'),
    axios.get('/api/dropdown_options/qualities')
  ])

  sourceTypes.value = sourceResponse.data || []
  qualities.value = qualityResponse.data || []
})

async function submit() {
  if (saving.value || !props.review.change_review_queue_id) return
  saving.value = true

  try {
    await axios.post(
      `/api/change_reviews/${props.review.change_review_queue_id}/citations`,
      {
        source: {
          source_type_code: Number(form.source_type_code),
          title: form.title,
          author: form.author || null,
          url: form.url || null
        },
        citation: {
          citation_text: form.citation_text,
          locator: form.locator || null,
          excerpt: form.excerpt || null,
          quality_code: Number(form.quality_code)
        }
      }
    )

    notifications.success('Source added to the pending change.')
    emit('saved')
  } catch (err) {
    notifications.error(err?.response?.data?.error || 'Unable to add the source.')
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <Modal
    :show="show"
    @close="emit('close')"
  >
    <template #title>
      Add a Source
    </template>

    <form
      class="stacked form gap-md"
      @submit.prevent="submit"
    >
      <p class="muted text">
        Your change was submitted for approval. Add supporting evidence now, or return to it later before approval.
      </p>

      <div class="two grid gap-md">
        <label class="field">
          Source Type<span class="required marker" aria-hidden="true">*</span>
          <select v-model="form.source_type_code" class="input" required>
            <option :value="null" disabled>Select a source type</option>
            <option
              v-for="sourceType in sourceTypes"
              :key="sourceType.source_type_code"
              :value="sourceType.source_type_code"
            >
              {{ sourceType.source_type }}
            </option>
          </select>
        </label>

        <label class="field">
          Evidence Quality<span class="required marker" aria-hidden="true">*</span>
          <select v-model="form.quality_code" class="input" required>
            <option
              v-for="quality in qualities"
              :key="quality.quality_code"
              :value="quality.quality_code"
            >
              {{ quality.quality }}
            </option>
          </select>
        </label>
      </div>

      <div class="two grid gap-md">
        <label class="field">
          Source Title<span class="required marker" aria-hidden="true">*</span>
          <input v-model.trim="form.title" class="input" type="text" required>
        </label>

        <label class="field">
          Author
          <input v-model.trim="form.author" class="input" type="text">
        </label>
      </div>

      <label class="field">
        URL
        <input v-model.trim="form.url" class="input" type="url">
      </label>

      <label class="field">
        Citation<span class="required marker" aria-hidden="true">*</span>
        <textarea v-model.trim="form.citation_text" class="input" rows="3" required />
      </label>

      <div class="two grid gap-md">
        <label class="field">
          Page or Locator
          <input v-model.trim="form.locator" class="input" type="text">
        </label>

        <label class="field">
          Relevant Excerpt
          <textarea v-model.trim="form.excerpt" class="input" rows="2" />
        </label>
      </div>

      <div class="end cluster gap-sm">
        <button class="secondary outline button" type="button" @click="emit('close')">
          Add Later
        </button>

        <button class="primary solid button" type="submit" :disabled="saving">
          {{ saving ? 'Adding…' : 'Add Source' }}
        </button>
      </div>
    </form>
  </Modal>
</template>
